import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Play, X, Puzzle, LayoutGrid, Grid3x3, Check, RotateCcw, Sparkles, Trophy,
  Trash2, Loader2, Upload, Wand2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import PageHeader from "@/components/PageHeader";
import StimulusCard from "@/components/StimulusCard";
import TokenBoard from "@/components/TokenBoard";
import TokenBoardControl from "@/components/TokenBoardControl";
import { operantColor } from "@/lib/operants";
import { categoryColor } from "@/lib/categoryColors";
import { playChime, playCelebration } from "@/lib/tokenSounds";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function ActivitiesPage() {
  const [learners, setLearners] = useState([]);
  const [learnerId, setLearnerId] = useState("");
  const [assignments, setAssignments] = useState({ targets: [], maintenance: [] });
  const [mode, setMode] = useState("match"); // "match" | "sort"
  const [matchStyle, setMatchStyle] = useState("identical"); // "identical" | "non_identical"
  const [matchInteraction, setMatchInteraction] = useState("drag"); // "drag" | "tap"
  const [arraySize, setArraySize] = useState("auto"); // "2" | "3" | "4" | "auto"
  const [stimulusFilter, setStimulusFilter] = useState("all"); // "all" | "real" | "illustration"
  const [session, setSession] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    http.get("/learners").then((r) => setLearners(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot mount fetch; http/setLearners are stable.
  }, []);
  useEffect(() => {
    if (!learnerId) { setAssignments({ targets: [], maintenance: [] }); return; }
    http.get(`/learners/${learnerId}/assignments`)
      .then((r) => setAssignments(r.data))
      .catch(() => setAssignments({ targets: [], maintenance: [] }));
  }, [learnerId]);

  // Per-learner array-size memory — the BCBA's last-used size sticks so
  // they don't have to re-select 3-card every session. Falls back to "auto".
  const [arraySizeRemembered, setArraySizeRemembered] = useState(false);
  useEffect(() => {
    if (!learnerId) { setArraySizeRemembered(false); return; }
    try {
      const saved = localStorage.getItem(`discrimina_array_size_${learnerId}`);
      if (saved && ["2", "3", "4", "auto"].includes(saved)) {
        setArraySize(saved);
        setArraySizeRemembered(true);
      } else {
        setArraySizeRemembered(false);
      }
    } catch (e) {
      /* localStorage unavailable (private mode, quota, disabled) — safe to skip; fall back to session default. */
      void e;
    }
  }, [learnerId]);
  useEffect(() => {
    if (!learnerId) return;
    try {
      localStorage.setItem(`discrimina_array_size_${learnerId}`, arraySize);
    } catch (e) {
      /* localStorage write failed (quota / private mode) — non-critical, next session just won't remember. */
      void e;
    }
  }, [arraySize, learnerId]);

  // Match / Sort default to *visual* stimuli (photo + illustration).
  // Text-based cards (Echoic prompts / Textual / imitation cues) can be optionally included
  // — useful for text-to-picture correspondence drills once a concept_id links them.
  const [includeText, setIncludeText] = useState(false);

  const pool = useMemo(() => {
    const raw = [
      ...assignments.targets.map((c) => ({ ...c, _status: "target" })),
      ...assignments.maintenance.map((c) => ({ ...c, _status: "maintenance" })),
    ];
    const visual = raw.filter(
      (c) => c.card_type === "photo" && (!!(c.image_data_url && c.image_data_url.trim()) || !!c.image_path),
    );
    const withText = includeText
      ? [...visual, ...raw.filter((c) => c.card_type !== "photo")]
      : visual;
    if (stimulusFilter === "all") return withText;
    return withText.filter((c) => (c.stimulus_style || "real") === stimulusFilter);
  }, [assignments, stimulusFilter, includeText]);

  // Text-only cards excluded when the toggle is off — used to show a helpful hint.
  const excludedCount = useMemo(() => {
    if (includeText) return 0;
    const all = [...(assignments.targets || []), ...(assignments.maintenance || [])];
    return all.filter((c) => c.card_type !== "photo").length;
  }, [assignments, includeText]);

  // ---------- Token economy state ----------
  const [reinforcerLabel, setReinforcerLabel] = useState("");
  const [reinforcerImage, setReinforcerImage] = useState(""); // data URL
  const [tokenCount, setTokenCount] = useState(5);
  const [tokens, setTokens] = useState([]); // array of identical data URLs
  const [tokenBusy, setTokenBusy] = useState(false);
  const fileRef = useRef(null);

  const onPickReinforcerImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please upload an image file.");
      return;
    }
    if (f.size > 3_000_000) {
      toast.error("Image too large — 3 MB max.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setReinforcerImage(reader.result);
    reader.readAsDataURL(f);
  };

  const generateTokens = async () => {
    const label = reinforcerLabel.trim();
    if (!label) {
      toast.error("Name the reinforcer first (e.g. 'iPad time', 'M&Ms').");
      return;
    }
    setTokenBusy(true);
    try {
      const r = await http.post("/generate-tokens", { reinforcer_label: label, count: tokenCount });
      setTokens(r.data.tokens || []);
      toast.success("Themed tokens ready!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not generate tokens");
    } finally { setTokenBusy(false); }
  };

  const clearReinforcer = () => {
    setReinforcerLabel("");
    setReinforcerImage("");
    setTokens([]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const start = async () => {
    if (pool.length < 2) {
      toast.error("Assign at least 2 cards to this learner first.");
      return;
    }
    if (mode === "match" && pool.length < 2) {
      toast.error("Need at least 2 cards for a match round.");
      return;
    }
    if (mode === "sort") {
      const catSet = new Set(pool.map((c) => (c.category || "Uncategorized")));
      if (catSet.size < 2) {
        toast.error("Sort mode needs cards from at least 2 different categories.");
        return;
      }
    }
    try {
      // Hydrate images for the runner right before we go into the drill —
      // /learners/{id}/assignments returns cards without image_data_url for speed;
      // batch-fetch the photo cards' images so the runner has real thumbnails.
      const photoIds = pool.filter((c) => (c.card_type || "photo") === "photo" && !c.image_data_url).map((c) => c.id);
      if (photoIds.length > 0) {
        try {
          const r = await http.post("/cards/images-by-ids", { ids: photoIds });
          const map = r.data?.images || {};
          setAssignments((prev) => ({
            targets: (prev.targets || []).map((c) => (map[c.id] ? { ...c, image_data_url: map[c.id] } : c)),
            maintenance: (prev.maintenance || []).map((c) => (map[c.id] ? { ...c, image_data_url: map[c.id] } : c)),
          }));
        } catch (e) { void e; }
      }
      // Local-only session — Discrimina doesn't persist runs, keeps only an in-memory id.
      const local = { id: (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())) };
      setSession(local);
      setRunning(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start session");
    }
  };

  const stop = async () => {
    setRunning(false);
    setSession(null);
  };

  if (running && session) {
    const tokenProps = {
      tokens, reinforcerImage, reinforcerLabel,
      setTokens, setReinforcerLabel, setReinforcerImage,
    };
    return mode === "match" ? (
      <MatchRunner
        pool={pool}
        session={session}
        learnerId={learnerId}
        matchStyle={matchInteraction === "tap" ? "non_identical" : matchStyle}
        interaction={matchInteraction}
        arraySizePref={arraySize}
        onExit={stop}
        {...tokenProps}
      />
    ) : (
      <SortRunner pool={pool} session={session} learnerId={learnerId} onExit={stop} {...tokenProps} />
    );
  }

  return (
    <div className="p-4 sm:p-8 md:p-12 max-w-4xl">
      <PageHeader
        eyebrow="Interactive"
        title="Activities"
        description="Present a card array with drag-to-match, or run a sort drill by category (Animals, Foods, Objects, Vehicles…). Photo ↔ illustration by default; toggle text-based cards for text-to-picture correspondence."
      />

      <Tabs value={mode} onValueChange={setMode}>
        <TabsList className="rounded-full bg-slate-100 mb-6">
          <TabsTrigger value="match" className="rounded-full data-[state=active]:bg-white" data-testid="mode-arrays">
            <LayoutGrid className="w-3.5 h-3.5 mr-1.5" /> Arrays
          </TabsTrigger>
          <TabsTrigger value="sort" className="rounded-full data-[state=active]:bg-white" data-testid="mode-sort">
            <Grid3x3 className="w-3.5 h-3.5 mr-1.5" /> Sort
          </TabsTrigger>
        </TabsList>

        <TabsContent value="match" className="space-y-6">
          <IntroCard
            title="Array drill"
            body="Two ways to run: Drag-to-match (target sits at the top, learner drags the matching card up) or Tap-to-select (SD from therapist — learner taps the card you name). 2–10 cards per round. Correct = fade + celebration overlay, wrong = shake + stays put. Auto-advances."
          />
          <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="match-interaction-panel">
            <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">How they respond</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setMatchInteraction("drag")}
                data-testid="match-interaction-drag"
                className={`h-14 rounded-xl border text-sm font-semibold transition-colors flex flex-col items-center justify-center leading-tight ${
                  matchInteraction === "drag"
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}
              >
                Drag to match
                <span className="text-[10px] font-normal opacity-70 mt-0.5">Target at top · drag correct card up</span>
              </button>
              <button
                onClick={() => setMatchInteraction("tap")}
                data-testid="match-interaction-tap"
                className={`h-14 rounded-xl border text-sm font-semibold transition-colors flex flex-col items-center justify-center leading-tight ${
                  matchInteraction === "tap"
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}
              >
                Tap to select
                <span className="text-[10px] font-normal opacity-70 mt-0.5">Therapist gives SD · learner taps card</span>
              </button>
            </div>
            {matchInteraction === "tap" && (
              <div className="mt-3 text-[11px] text-slate-500 leading-relaxed">
                Tap mode runs as a Listener Response drill. The array is drawn from concept groups so
                the correct card is never a duplicate of another on screen — matching style is
                auto-locked to <strong>non-identical</strong>.
              </div>
            )}
          </div>
          <div className={`rounded-2xl border border-slate-200 bg-white p-4 ${matchInteraction === "tap" ? "opacity-50 pointer-events-none select-none" : ""}`}>
            <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">Matching style</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setMatchStyle("identical")}
                data-testid="match-style-identical"
                className={`h-11 rounded-xl border text-sm font-semibold transition-colors ${
                  matchStyle === "identical"
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}
              >
                Identical
                <div className="text-[10px] font-normal opacity-70 mt-0.5">Match the exact card</div>
              </button>
              <button
                onClick={() => setMatchStyle("non_identical")}
                data-testid="match-style-non-identical"
                className={`h-11 rounded-xl border text-sm font-semibold transition-colors ${
                  matchStyle === "non_identical"
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}
              >
                Non-identical
                <div className="text-[10px] font-normal opacity-70 mt-0.5">Match by shared concept</div>
              </button>
            </div>
            {matchStyle === "non_identical" && (
              <div className="mt-3 text-[11px] text-slate-500 leading-relaxed">
                Requires cards with a shared <strong>concept group</strong> (set on each card in the Card Library).
                E.g. an "apple photo" and an "apple illustration" both tagged concept = <code>apple</code>.
              </div>
            )}
          </div>

          {/* Array size picker */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Array size</div>
              <div className="flex items-center gap-2">
                {arraySizeRemembered && learnerId && (
                  <span
                    className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700"
                    data-testid="array-size-remembered-pill"
                    title="Loaded from this learner's last session"
                  >
                    Remembered
                  </span>
                )}
                <div className="text-[10px] text-slate-400">Cards shown per round (excluding target)</div>
              </div>
            </div>
            <div className="grid grid-cols-5 sm:grid-cols-10 gap-1.5">
              {["2", "3", "4", "5", "6", "7", "8", "9", "10", "auto"].map((id) => {
                const label = id === "auto" ? "Auto" : id;
                const hint = id === "auto" ? "fit pool" : id === "2" ? "simple" : id === "10" ? "max" : "";
                return (
                  <button
                    key={id}
                    onClick={() => setArraySize(id)}
                    data-testid={`array-size-${id}`}
                    className={`h-14 rounded-xl border font-bold transition-colors flex flex-col items-center justify-center leading-tight ${
                      arraySize === id
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <span className={id === "auto" ? "text-xs" : "text-lg"}>{label}</span>
                    {hint && (
                      <span className={`text-[9px] font-normal mt-0.5 ${arraySize === id ? "opacity-80" : "opacity-60"}`}>{hint}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </TabsContent>
        <TabsContent value="sort" className="space-y-6">
          <IntroCard
            title="Sorting drill"
            body="Category buckets appear at the top (Animals, Foods, Objects, Vehicles…). Cards drop in one at a time — tap the bucket that matches the card's category."
          />
        </TabsContent>
      </Tabs>

      {/* Stimulus style filter */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 mb-4">
        <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">Stimulus style</div>
        <div className="flex gap-2">
          {[
            { id: "all", label: "All" },
            { id: "real", label: "Real photos" },
            { id: "illustration", label: "Illustrations" },
          ].map((s) => (
            <button
              key={s.id}
              onClick={() => setStimulusFilter(s.id)}
              data-testid={`stim-filter-${s.id}`}
              className={`flex-1 h-9 rounded-lg text-xs font-semibold border transition-colors ${
                stimulusFilter === s.id
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <label className="mt-3 flex items-center gap-2 cursor-pointer text-xs text-slate-600" data-testid="include-text-toggle-row">
          <input
            type="checkbox"
            checked={includeText}
            onChange={(e) => setIncludeText(e.target.checked)}
            data-testid="include-text-toggle"
            className="h-4 w-4 rounded border-slate-300"
          />
          <span>
            Also include <strong>text-based cards</strong> (Echoic / Textual / Intraverbal / Imitation cues)
            <span className="block text-[10px] text-slate-400">Use for text ↔ picture correspondence drills. Requires a shared concept id.</span>
          </span>
        </label>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 space-y-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">Learner</div>
          <Select value={learnerId} onValueChange={setLearnerId}>
            <SelectTrigger className="rounded-xl h-11" data-testid="activity-learner-select">
              <SelectValue placeholder="Pick a learner" />
            </SelectTrigger>
            <SelectContent>
              {learners.map((l) => (
                <SelectItem key={l.id} value={l.id}>{l.chart_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {learnerId && (
          <div className="text-sm text-slate-600" data-testid="activity-pool-summary">
            <span className="font-bold text-slate-900">{pool.length}</span> photo card{pool.length === 1 ? "" : "s"} available
            {mode === "sort" && (
              <span className="text-slate-500">
                {" · "}
                {[...new Set(pool.map((c) => c.category || "Uncategorized"))].length} categor{[...new Set(pool.map((c) => c.category || "Uncategorized"))].length === 1 ? "y" : "ies"}
              </span>
            )}
            {excludedCount > 0 && (
              <div className="mt-1 text-[11px] text-slate-500" data-testid="activity-excluded-hint">
                {excludedCount} text-based card{excludedCount === 1 ? "" : "s"} (Echoic / Textual / Imitation / etc.) hidden — Match &amp; Sort need images.
              </div>
            )}
          </div>
        )}

        <Button
          onClick={start}
          disabled={!learnerId || pool.length < 2}
          className="rounded-full h-12 px-6"
          data-testid="activity-start-button"
        >
          <Play className="w-4 h-4 mr-2" /> Start {mode === "match" ? "array" : "sorting"}
        </Button>
      </div>

      {/* Token Economy setup */}
      <div className="mt-6 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5" data-testid="token-economy-setup">
        <div className="flex items-center gap-2 mb-1">
          <Trophy className="w-4 h-4 text-amber-600" />
          <div className="font-display font-extrabold text-lg">Token economy <span className="text-slate-400 font-normal text-sm">— optional</span></div>
        </div>
        <p className="text-xs text-slate-600 mb-4 leading-relaxed">
          Show the learner what they're working toward. Pick a reinforcer, upload its photo, and Discrimina will AI-generate matching tokens. The board fills as they respond correctly.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Reinforcer name</label>
            <Input
              value={reinforcerLabel}
              onChange={(e) => { setReinforcerLabel(e.target.value); if (tokens.length) setTokens([]); }}
              placeholder="iPad time · M&Ms · Bubbles · Trampoline"
              maxLength={80}
              data-testid="reinforcer-label-input"
              className="rounded-xl"
            />
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 mt-3 block">Reinforcer photo</label>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={onPickReinforcerImage}
              className="text-xs text-slate-600"
              data-testid="reinforcer-file-input"
            />
            <div className="mt-3 flex items-center gap-2">
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Tokens</label>
              <Select value={String(tokenCount)} onValueChange={(v) => { setTokenCount(parseInt(v, 10)); setTokens([]); }}>
                <SelectTrigger className="w-20 h-8 rounded-xl text-xs" data-testid="token-count-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[3, 4, 5, 6, 7, 8, 10].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button
                type="button"
                onClick={generateTokens}
                disabled={tokenBusy || !reinforcerLabel.trim()}
                size="sm"
                className="rounded-full ml-auto"
                data-testid="generate-tokens-button"
              >
                {tokenBusy ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Generating…</> : <><Wand2 className="w-3.5 h-3.5 mr-1.5" /> {tokens.length ? "Regenerate" : "Generate"} tokens</>}
              </Button>
            </div>
          </div>
          <div className="flex flex-col items-center justify-center bg-white rounded-2xl border border-amber-100 p-3 min-h-[160px]">
            {(reinforcerImage || tokens.length > 0) ? (
              <div className="w-full flex items-center justify-around">
                <div className="text-center">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">Reinforcer</div>
                  {reinforcerImage ? (
                    <img src={reinforcerImage} alt={reinforcerLabel} className="w-20 h-20 object-cover rounded-2xl ring-2 ring-emerald-300" data-testid="reinforcer-preview-image" />
                  ) : (
                    <div className="w-20 h-20 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 text-xs px-2 text-center">
                      Optional photo
                    </div>
                  )}
                  <div className="text-xs font-semibold text-slate-800 mt-1.5 max-w-[6rem] truncate">{reinforcerLabel || "—"}</div>
                </div>
                <div className="text-center">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">Tokens ×{tokenCount}</div>
                  <div className="flex gap-1">
                    {tokens.length > 0 ? (
                      <img src={tokens[0]} alt="token" className="w-12 h-12 object-contain rounded-xl bg-amber-50 ring-2 ring-amber-200" data-testid="token-preview-image" />
                    ) : (
                      <div className="w-12 h-12 rounded-xl bg-slate-100 border-2 border-dashed border-slate-300" />
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={clearReinforcer}
                  aria-label="Clear reinforcer"
                  className="text-slate-400 hover:text-rose-600 self-start"
                  data-testid="clear-reinforcer-button"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="text-center text-xs text-slate-500 leading-relaxed max-w-[240px]">
                <Upload className="w-5 h-5 mx-auto mb-1 text-slate-400" />
                No reinforcer set yet — start plain or set one above to unlock the token board.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function IntroCard({ title, body }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-5">
      <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">
        <Sparkles className="w-3 h-3" /> How it works
      </div>
      <div className="font-display text-xl font-bold text-slate-900 mb-1.5">{title}</div>
      <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

/* --------------------------- MATCH RUNNER --------------------------- */
function MatchRunner({ pool, session, learnerId, matchStyle, interaction = "drag", arraySizePref = "auto", onExit, tokens = [], reinforcerImage = "", reinforcerLabel = "", setTokens, setReinforcerLabel, setReinforcerImage }) {
  const [round, setRound] = useState(0);
  const [choices, setChoices] = useState([]);
  const [target, setTarget] = useState(null);
  const [correctChoiceId, setCorrectChoiceId] = useState(null); // for non-identical mode
  const [feedback, setFeedback] = useState(null);
  const [wrongCardId, setWrongCardId] = useState(null); // for shake animation
  const [matched, setMatched] = useState(false); // triggers fade-out + "Matched!" overlay
  const [score, setScore] = useState({ correct: 0, incorrect: 0 });
  const [locked, setLocked] = useState(false);
  const [tokensEarned, setTokensEarned] = useState(0);
  const [celebrating, setCelebrating] = useState(false);
  const targetRef = useRef(null);

  // Reset earned tokens whenever the reinforcer is switched mid-session
  useEffect(() => { setTokensEarned(0); }, [tokens.length, reinforcerLabel]);

  const triggerCelebration = () => {
    setCelebrating(true);
    playCelebration();
    setTimeout(() => { setCelebrating(false); setTokensEarned(0); }, 2600);
  };

  // Honor BCBA's pinned array size when possible; fall back to "auto" (min(10, pool)).
  const arraySize = arraySizePref === "auto"
    ? Math.min(10, pool.length)
    : Math.min(parseInt(arraySizePref, 10), pool.length);

  const nextRound = () => {
    setLocked(false);
    setFeedback(null);
    setWrongCardId(null);
    setMatched(false);
    if (matchStyle === "non_identical") {
      // Find pairs (cards sharing a concept_id)
      const byConcept = {};
      pool.forEach((c) => {
        const cid = (c.concept_id || "").trim().toLowerCase();
        if (!cid) return;
        (byConcept[cid] = byConcept[cid] || []).push(c);
      });
      const pairs = Object.values(byConcept).filter((arr) => arr.length >= 2);
      if (pairs.length === 0) {
        // Fallback: no concept groups → treat as identical for this round
        const picks = shuffle(pool).slice(0, arraySize);
        const t = picks[Math.floor(Math.random() * picks.length)];
        setChoices(picks);
        setTarget(t);
        setCorrectChoiceId(t.id);
        setRound((r) => r + 1);
        return;
      }
      const pairGroup = pairs[Math.floor(Math.random() * pairs.length)];
      const shuffled = shuffle(pairGroup);
      const t = shuffled[0];
      const correctPartner = shuffled[1];
      const distractors = shuffle(pool.filter((c) => !pairGroup.some((p) => p.id === c.id))).slice(0, arraySize - 1);
      const finalChoices = shuffle([correctPartner, ...distractors]);
      setChoices(finalChoices);
      setTarget(t);
      setCorrectChoiceId(correctPartner.id);
    } else {
      const picks = shuffle(pool).slice(0, arraySize);
      const t = picks[Math.floor(Math.random() * picks.length)];
      setChoices(picks);
      setTarget(t);
      setCorrectChoiceId(t.id);
    }
    setRound((r) => r + 1);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- nextRound is stable within this session; only mount fires it.
  useEffect(() => { nextRound(); }, []);

  const record = async (card_id, result) => {
    // No-op: Discrimina no longer logs trial data. Feedback stays local.
    void card_id; void result;
  };

  const handleCorrect = async (card) => {
    setMatched(true);
    setFeedback({ cardId: card.id, correct: true });
    setScore((s) => ({ ...s, correct: s.correct + 1 }));
    if (tokens.length > 0) {
      setTokensEarned((prev) => {
        const next = prev + 1;
        if (next >= tokens.length) { triggerCelebration(); return tokens.length; }
        playChime();
        return next;
      });
    } else {
      playChime();
    }
    await record(target.id, "correct");
    setTimeout(nextRound, 1200);
  };

  const handleWrong = async (card) => {
    setWrongCardId(card.id);
    setFeedback({ cardId: card.id, correct: false });
    setScore((s) => ({ ...s, incorrect: s.incorrect + 1 }));
    toast("Try again", { icon: "🤔", duration: 1400 });
    await record(target.id, "incorrect");
    setTimeout(() => {
      setWrongCardId(null);
      setFeedback(null);
      setLocked(false);
    }, 900);
  };

  // Called by each draggable choice when the drag ends
  const handleDrop = (card, info) => {
    if (locked || matched) return;
    const tRect = targetRef.current?.getBoundingClientRect();
    if (!tRect) return;
    const { x, y } = info.point;
    const hit = x >= tRect.left && x <= tRect.right && y >= tRect.top && y <= tRect.bottom;
    if (!hit) return; // snaps back automatically via dragSnapToOrigin
    setLocked(true);
    if (card.id === correctChoiceId) handleCorrect(card);
    else handleWrong(card);
  };

  if (!target) return null;
  const tc = operantColor(target.verbal_operant);

  return (
    <div className="fixed inset-0 bg-slate-50 z-50 overflow-y-auto overscroll-contain" data-testid="match-runner">
      <TokenBoard
        earned={tokensEarned}
        tokens={tokens}
        reinforcerImage={reinforcerImage}
        reinforcerLabel={reinforcerLabel}
        celebrating={celebrating}
      />
      <TokenBoardControl
        tokens={tokens} reinforcerImage={reinforcerImage} reinforcerLabel={reinforcerLabel}
        setTokens={setTokens} setReinforcerLabel={setReinforcerLabel} setReinforcerImage={setReinforcerImage}
        tokensEarned={tokensEarned} setTokensEarned={setTokensEarned}
        onCelebrate={triggerCelebration}
      />
      {/* Header — sticks to top so exit + score stay reachable while scrolling */}
      <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
            Array · Round {round}
          </div>
          <div className="font-display font-bold text-lg text-slate-900">
            {interaction === "tap" ? `Touch the ${target.label}` : "Drag a card onto the target"}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <ScoreChip label="Correct" value={score.correct} tone="emerald" testId="match-score-correct" />
          <ScoreChip label="Incorrect" value={score.incorrect} tone="rose" testId="match-score-incorrect" />
          <button
            onClick={onExit}
            className="rounded-full bg-slate-900 text-white h-10 w-10 flex items-center justify-center hover:bg-slate-800"
            data-testid="match-exit-button"
            aria-label="Exit"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Target + Choices — side-by-side on tablet+ so the learner sees both without scrolling.
          In `tap` mode we replace the drag-target card with a big spoken-SD banner. */}
      <div className="flex flex-col md:flex-row gap-4 md:gap-6 px-4 md:px-6 pt-4 pb-24 max-w-6xl mx-auto">
        {interaction === "tap" ? (
          <div className="md:w-64 md:shrink-0 flex md:block justify-center" data-testid="match-sd-banner">
            <motion.div
              key={`sd-${round}`}
              initial={{ opacity: 0, y: -8 }}
              animate={matched ? { opacity: 0, scale: 0.9 } : { opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="rounded-2xl border-2 shadow-md overflow-hidden w-full max-w-xs px-6 py-6 text-center"
              style={{ background: tc.bg, borderColor: tc.border }}
            >
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] mb-2" style={{ color: tc.text }}>
                Therapist says
              </div>
              <div className="font-display text-2xl md:text-3xl font-extrabold leading-tight" style={{ color: tc.text }}>
                “Touch the {target.label}”
              </div>
              <div className="mt-3 text-[10px] uppercase tracking-wider text-slate-500">
                Wait for tap · learner responds
              </div>
            </motion.div>
          </div>
        ) : (
          <div className="md:w-64 md:shrink-0 flex md:block justify-center">
            <motion.div
              ref={targetRef}
              animate={matched ? { opacity: 0, scale: 0.85 } : { opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="rounded-2xl border-4 shadow-md overflow-hidden relative"
              style={{ background: tc.bg, borderColor: tc.border }}
              data-testid="match-target"
            >
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] px-4 pt-2 pb-0.5 text-center" style={{ color: tc.text }}>
                Drop here
              </div>
              <div className="px-3 pb-3 pt-1 w-36 sm:w-44 md:w-56">
                <StimulusCard card={target} size="md" testId="match-target-card" showSubtext={false} />
              </div>
            </motion.div>
          </div>
        )}

      {/* Matched! overlay */}
      <AnimatePresence>
        {matched && (
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: "spring", damping: 14 }}
            className="fixed inset-0 flex items-center justify-center pointer-events-none z-[55]"
            data-testid="match-matched-overlay"
          >
            <div className="bg-emerald-600 text-white rounded-3xl px-10 py-6 shadow-2xl flex items-center gap-3">
              <Check className="w-8 h-8" strokeWidth={3} />
              <span className="font-display text-3xl font-extrabold tracking-tight">Matched!</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

        {/* Choices grid — draggable */}
        <div className="flex-1 min-w-0">
        <div className={`grid gap-2 sm:gap-3 ${
          arraySize <= 2 ? "grid-cols-2"
            : arraySize === 3 ? "grid-cols-3"
            : arraySize === 4 ? "grid-cols-2 sm:grid-cols-4"
            : arraySize <= 6 ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6"
            : arraySize <= 8 ? "grid-cols-2 sm:grid-cols-4"
            : "grid-cols-2 sm:grid-cols-5"
        }`}>
          <AnimatePresence>
            {choices.map((c) => {
              const isCorrectMatched = matched && c.id === correctChoiceId;
              const isWrongShaking = wrongCardId === c.id;
              const dragProps = interaction === "drag"
                ? {
                    drag: !locked && !matched,
                    dragSnapToOrigin: true,
                    dragElastic: 0.6,
                    dragMomentum: false,
                    whileDrag: { scale: 1.08, zIndex: 60, boxShadow: "0 20px 40px rgba(0,0,0,0.25)" },
                    onDragEnd: (_e, info) => handleDrop(c, info),
                  }
                : {
                    onClick: () => {
                      if (locked || matched) return;
                      setLocked(true);
                      if (c.id === correctChoiceId) handleCorrect(c);
                      else handleWrong(c);
                    },
                    whileTap: { scale: 0.95 },
                  };
              return (
                <motion.div
                  key={`${round}-${c.id}`}
                  {...dragProps}
                  initial={{ opacity: 0, y: 20 }}
                  animate={
                    isCorrectMatched
                      ? { opacity: 0, scale: 0.85, transition: { duration: 0.4 } }
                      : isWrongShaking
                      ? { x: [0, -10, 10, -8, 8, -4, 4, 0], transition: { duration: 0.5 } }
                      : { opacity: 1, y: 0 }
                  }
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`relative rounded-2xl touch-none ${
                    interaction === "drag" ? "cursor-grab active:cursor-grabbing" : "cursor-pointer active:brightness-95"
                  } ${isWrongShaking ? "ring-4 ring-rose-400 ring-offset-2" : ""}`}
                  data-testid={`match-choice-wrap-${c.id}`}
                >
                  <StimulusCard
                    card={c}
                    size={arraySize >= 7 ? "sm" : "md"}
                    testId={`match-choice-${c.id}`}
                    showSubtext={false}
                  />
                  {isWrongShaking && (
                    <div className="absolute inset-0 flex items-center justify-center bg-rose-500/20 rounded-2xl pointer-events-none">
                      <div className="bg-rose-600 text-white rounded-full w-14 h-14 flex items-center justify-center shadow-xl">
                        <X className="w-7 h-7" strokeWidth={3} />
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
        <div className="mt-4 text-center text-xs text-slate-500">
          {interaction === "tap" ? "Learner taps the card the therapist named." : "Drag a card up onto the target to match."}
        </div>
        </div>
      </div>

      <style>{`@keyframes shake {0%,100%{transform:translateX(0)}25%{transform:translateX(-6px)}75%{transform:translateX(6px)}}`}</style>
    </div>
  );
}

/* --------------------------- SORT RUNNER --------------------------- */
function SortRunner({ pool, session, learnerId, onExit, tokens = [], reinforcerImage = "", reinforcerLabel = "", setTokens, setReinforcerLabel, setReinforcerImage }) {
  // Buckets = distinct verbal operants in the pool
  const buckets = useMemo(() => [...new Set(pool.map((c) => c.verbal_operant))], [pool]);
  const [queue, setQueue] = useState(() => shuffle(pool));
  const [placed, setPlaced] = useState({}); // bucketOp -> [card,...]
  const [score, setScore] = useState({ correct: 0, incorrect: 0 });
  const [flash, setFlash] = useState(null); // { bucket, ok }
  const [locked, setLocked] = useState(false);
  const [tokensEarned, setTokensEarned] = useState(0);
  const [celebrating, setCelebrating] = useState(false);

  useEffect(() => { setTokensEarned(0); }, [tokens.length, reinforcerLabel]);

  const triggerCelebration = () => {
    setCelebrating(true);
    playCelebration();
    setTimeout(() => { setCelebrating(false); setTokensEarned(0); }, 2600);
  };

  const current = queue[0] || null;
  const done = !current;

  const record = async (card_id, result) => {
    // No-op: Discrimina no longer logs trial data. Feedback stays local.
    void card_id; void result;
  };

  const drop = async (bucketCat) => {
    if (locked || !current) return;
    setLocked(true);
    const ok = (current.category || "Uncategorized") === bucketCat;
    setFlash({ bucket: bucketCat, ok });
    setScore((s) => ok
      ? { ...s, correct: s.correct + 1 }
      : { ...s, incorrect: s.incorrect + 1 });
    setPlaced((p) => ({ ...p, [bucketCat]: [...(p[bucketCat] || []), current] }));
    // Token board — fills on every correct sort, celebrates + resets when full.
    if (ok && tokens.length > 0) {
      setTokensEarned((prev) => {
        const next = prev + 1;
        if (next >= tokens.length) {
          triggerCelebration();
          return tokens.length;
        }
        playChime();
        return next;
      });
    } else if (ok) {
      playChime();
    }
    await record(current.id, ok ? "correct" : "incorrect");
    setTimeout(() => {
      setQueue((q) => q.slice(1));
      setFlash(null);
      setLocked(false);
    }, 700);
  };

  const reset = () => {
    setQueue(shuffle(pool));
    setPlaced({});
    setScore({ correct: 0, incorrect: 0 });
    setFlash(null);
    setLocked(false);
  };

  return (
    <div className="fixed inset-0 bg-slate-50 z-50 overflow-y-auto overscroll-contain" data-testid="sort-runner">
      <TokenBoard
        earned={tokensEarned}
        tokens={tokens}
        reinforcerImage={reinforcerImage}
        reinforcerLabel={reinforcerLabel}
        celebrating={celebrating}
      />
      <TokenBoardControl
        tokens={tokens} reinforcerImage={reinforcerImage} reinforcerLabel={reinforcerLabel}
        setTokens={setTokens} setReinforcerLabel={setReinforcerLabel} setReinforcerImage={setReinforcerImage}
        tokensEarned={tokensEarned} setTokensEarned={setTokensEarned}
        onCelebrate={triggerCelebration}
      />
      <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
            Sort · {pool.length - queue.length} / {pool.length}
          </div>
          <div className="font-display font-bold text-lg text-slate-900">Drop each card into the right category</div>
        </div>
        <div className="flex items-center gap-4">
          <ScoreChip label="Correct" value={score.correct} tone="emerald" testId="sort-score-correct" />
          <ScoreChip label="Incorrect" value={score.incorrect} tone="rose" testId="sort-score-incorrect" />
          <button
            onClick={onExit}
            className="rounded-full bg-slate-900 text-white h-10 w-10 flex items-center justify-center hover:bg-slate-800"
            data-testid="sort-exit-button"
            aria-label="Exit"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Buckets */}
      <div className="p-4 sm:p-6 border-b border-slate-200 bg-white">
        <div
          className="grid gap-2 sm:gap-3"
          style={{ gridTemplateColumns: `repeat(${Math.min(Math.max(buckets.length, 2), 5)}, minmax(0, 1fr))` }}
        >
          {buckets.map((cat) => {
            const oc = categoryColor(cat);
            const items = placed[cat] || [];
            const isFlash = flash && flash.bucket === cat;
            return (
              <button
                key={cat}
                onClick={() => drop(cat)}
                disabled={locked || done}
                data-testid={`sort-bucket-${cat}`}
                className={`rounded-2xl border-2 border-dashed p-2.5 sm:p-3 min-h-[96px] flex flex-col items-center text-center transition-all disabled:cursor-default ${
                  isFlash && flash.ok ? "ring-4 ring-emerald-400" : ""
                } ${isFlash && !flash.ok ? "ring-4 ring-rose-400" : ""}`}
                style={{ borderColor: oc.border, background: oc.bg }}
              >
                <div
                  className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full mb-1 whitespace-nowrap"
                  style={{ background: oc.border, color: "white" }}
                >
                  {cat}
                </div>
                <div className="text-xs text-slate-500">{items.length} card{items.length === 1 ? "" : "s"}</div>
                {items.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap justify-center gap-1">
                    {items.slice(-3).map((it) => (
                      <span key={it.id} className="text-[10px] px-1.5 py-0.5 bg-white rounded-md border border-slate-200 text-slate-600 truncate max-w-[80px]">
                        {it.label}
                      </span>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Current card in play */}
      <div className="flex flex-col items-center justify-center p-6 pb-24 min-h-[50vh]">
        {done ? (
          <FinalScreen score={score} onReset={reset} onExit={onExit} />
        ) : (
          <>
            <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">
              Sort this card
            </div>
            <div className="w-56 sm:w-64 max-w-full">
              <StimulusCard card={current} size="lg" testId="sort-current" showSubtext={false} />
            </div>
            <div className="mt-4 text-xs text-slate-500">
              Tap the bucket above that matches this card's category.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ScoreChip({ label, value, tone, testId }) {
  const cls = {
    emerald: "bg-emerald-100 text-emerald-700",
    rose:    "bg-rose-100 text-rose-700",
  }[tone];
  return (
    <div className={`px-3 py-1.5 rounded-full text-xs font-bold ${cls}`} data-testid={testId}>
      {label}: <span className="tabular-nums">{value}</span>
    </div>
  );
}

function FinalScreen({ score, onReset, onExit }) {
  const total = score.correct + score.incorrect;
  const pct = total > 0 ? Math.round((score.correct / total) * 100) : 0;
  return (
    <div className="text-center max-w-md">
      <div className="w-16 h-16 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto mb-4">
        <Trophy className="w-7 h-7" />
      </div>
      <div className="font-display text-4xl font-extrabold">Nice work!</div>
      <div className="mt-2 text-slate-600">
        {score.correct} / {total} correct · <span className="font-bold text-slate-900">{pct}%</span>
      </div>
      <div className="mt-6 flex gap-2 justify-center">
        <Button onClick={onReset} variant="outline" className="rounded-full" data-testid="sort-restart">
          <RotateCcw className="w-4 h-4 mr-2" /> Play again
        </Button>
        <Button onClick={onExit} className="rounded-full" data-testid="sort-finish">
          Done
        </Button>
      </div>
    </div>
  );
}
